import { Box } from '@mui/material';
import { InfoCircle } from '@styled-icons/bootstrap';
import { Plus } from '@styled-icons/bootstrap/Plus';
import { Trash } from '@styled-icons/bootstrap/Trash';
import { Edit } from '@styled-icons/boxicons-solid/Edit';
import { DialogDefault, ThemeTypography } from 'DesignSystem';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import { ELanguage, TLanguage } from 'DesignSystem/types/language.types';
import StepSelectorBlock from 'FeatureModules/Activity/Components/Forms/StepSelectorBlock';
import {
  activitySchema,
  IActivityFormValue,
} from 'FeatureModules/Activity/schemas/activities.schema';
import { IActivity } from 'FeatureModules/Activity/types/activity.types';
import { extractActivityMutationValues } from 'FeatureModules/Activity/utils/activity.utils';
import useComponentChainActions from 'FeatureModules/ComponentChain/hooks/useComponentChainActions';
import useComponentItemActions from 'FeatureModules/ComponentChain/hooks/useComponentItemActions';
import chainActivitySchema, {
  IChainActivityFormValue,
  nestedActivitySchema,
} from 'FeatureModules/ComponentChain/schemas/chains.schema';
import {
  IChainActivity,
  IChainStep,
  ISubChain,
} from 'FeatureModules/ComponentChain/types/chain.types';
import { usePartnerMutations } from 'FeatureModules/Partner/Components/hooks';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import useSiteMutations from 'FeatureModules/Site/hooks/useSiteMutations';
import { IBaseSite } from 'FeatureModules/Site/types/site.types';
import { Form, Formik, FormikProps } from 'formik';
import useDialog from 'hooks/useDialog';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import React, { FC, useRef } from 'react';
import { IDefaultDialogProps } from 'types/dialog.types';
import { difference } from 'utils/object.utils';
import ActivityCommunicationBlocks from '../Forms/ActivityCommunicationBlocks';
import CompanySelectBlock from '../Forms/CompanySelectBlock';
import LinkConnectionsBlock from '../Forms/LinkConnectionsBlock';
import OutputComponentBlock from '../Forms/OutputComponentBlock';
import SiteSelectorBlock from '../Forms/SiteSelectorBlock';
import {
  BlockContainer,
  BottomContainer,
  ButtonContainer,
  DialogInner,
  FormContainerLeft,
  FormContainerRight,
  StyledLink,
} from '../Forms/styles';

type TAddEditActivity =
  | {
      mode: 'add-activity';
      /**
       * The site selector will be preselected and disabled
       */
      site?: IBaseSite;
      /**
       * The partner / company selector will be disabled and the partner can be preselected with the partner prop
       */
      partner?: IBasePartnerCompany;
    }
  | {
      mode: 'edit-activity';
      /**
       * Location to be edited
       */
      activity: IActivity;
      /**
       * In this language the activity will be edited
       * Default is English
       */
      lang?: TLanguage;
      /**
       * Shown on the communication side where you are not able to edit chain related information
       */
      linkToSupplyChainMapping?: string;
      /**
       * For mode default the actual properties can be edited and changed
       * In 'translate' only the communication values like translations and media can be edited
       */
      editMode?: 'default' | 'translate';
    };

type TAddEditChainActivity =
  | {
      mode: 'add-chain-activity';
      /**
       * The step where the activity will be added to
       */
      selectedStepId: string;
      activities: IChainActivity[];
      subChains: ISubChain[];
      steps: IChainStep[];
      partner?: IBasePartnerCompany;
      onActivityAdd: (
        activityId: string,
        activity: Pick<IChainActivity, 'chainStepId' | 'incomingLinks'>
      ) => void;
    }
  | {
      mode: 'edit-chain-activity';
      activities: IChainActivity[];
      subChains: ISubChain[];
      steps: IChainStep[];
      /**
       * Activity to be edited
       */
      chainStepActivity: Omit<IChainActivity, 'pointPosition'>;
      onChainActivityEdit: (
        activity: Pick<IChainActivity, 'id'> &
          Partial<Pick<IChainActivity, 'chainStepId' | 'incomingLinks'>> // Will only be send if changed to avoid unnecessary updates
      ) => void;
      onActivityDelete: (activityId: string) => void;
    };

export type IAddEditActivityDialogProps = {
  disableDelete?: boolean;
  hidePartnerSelector?: boolean;
} & IDefaultDialogProps &
  (TAddEditChainActivity | TAddEditActivity);

enum EAddEditActivityMode {
  ADD_ACTIVITY = 'add-activity',
  EDIT_ACTIVITY = 'edit-activity',
  ADD_CHAIN_ACTIVITY = 'add-chain-activity',
  EDIT_CHAIN_ACTIVITY = 'edit-chain-activity',
}

type TFormValues =
  | IChainActivityFormValue
  | {
      activity?: IActivityFormValue;
    };

/**
 * The dialog can be opened in different modes controlling which information is shown and can be edited or created
 * AddEditActivityMode.ADD_ACTIVITY | AddEditActivityMode.EDIT_ACTIVITY | AddEditActivityMode.ADD_CHAIN_ACTIVITY | AddEditActivityMode.EDIT_CHAIN_ACTIVITY;
 */
const AddEditActivityDialog: FC<IAddEditActivityDialogProps> = ({
  open,
  disableDelete,
  hidePartnerSelector,
  onClose,
  ...props
}) => {
  const formRef = useRef<FormikProps<TFormValues>>(null);
  const { openDialog } = useDialog();
  const { handleCreateActivity, handleRemoveActivity } = usePartnerMutations();
  const { updateActivity } = useComponentChainActions();
  const { updateComponent } = useComponentItemActions();
  const { updateSite } = useSiteMutations();

  const mode = props.mode;
  const isChainActivity =
    mode === EAddEditActivityMode.ADD_CHAIN_ACTIVITY ||
    mode === EAddEditActivityMode.EDIT_CHAIN_ACTIVITY;
  const isEdit =
    mode === EAddEditActivityMode.EDIT_CHAIN_ACTIVITY ||
    mode === EAddEditActivityMode.EDIT_ACTIVITY;
  const editMode = 'editMode' in props ? props.editMode : 'default';

  const initialData: TFormValues = isChainActivity
    ? {
        ...chainActivitySchema.default(),
        ...(isEdit
          ? {
              ...omit(props.chainStepActivity, 'pointPosition'),
              activity: {
                ...activitySchema.default(),
                ...omit(props.chainStepActivity.activity, ['latestLog', 'createdTimestamp']),
                subActivities: props.chainStepActivity.activity.subActivities?.length
                  ? props.chainStepActivity.activity.subActivities
                  : activitySchema.default().subActivities,
              },
            }
          : {
              chainStepId: props.selectedStepId,
              activity: {
                ...activitySchema.default(),
                ownedBy: props.partner,
              },
            }),
      }
    : {
        activity: {
          ...activitySchema.default(),
          // Preselect the site if it is given and the owner of the activity to the same of the owner of the site. This can be overridden by the partner prop and by the user
          ...(!isEdit && props.site ? { site: props.site, ownedBy: props.site.ownedBy } : {}),
          ...(isEdit
            ? {
                ...omit(props.activity, ['latestLog', 'createdTimestamp']),
                subActivities: props.activity.subActivities?.length
                  ? props.activity.subActivities
                  : activitySchema.default().subActivities,
              }
            : {}),
          ...(!isEdit && props.partner ? { ownedBy: props.partner } : {}),
        },
      };

  const handleSubmit = async (values: TFormValues) => {
    let activityResponse;

    const subActivities =
      values.activity?.subActivities?.length === 1 && values.activity?.subActivities[0].title === ''
        ? []
        : values.activity?.subActivities;

    if (isEdit && 'activity' in values && values.activity && initialData.activity) {
      // If we are editing the communication of an activity and the component title has changed, we need to update the correct component language
      if (editMode === 'translate') {
        // Trigger update of the output component if the title has changed
        if (
          values.activity.component &&
          values.activity.component.title !== (initialData.activity as IActivity).component?.title
        ) {
          updateComponent({
            variables: {
              id: values.activity.component.id,
              input: {
                ...omit(values.activity.component, ['id', 'rawMaterials']),
                rawMaterialIds: values.activity.component.rawMaterials.map(({ id }) => id),
              },
              lang: ('lang' in props && props.lang) || ELanguage.ENGLISH,
            },
          });
        }

        if (
          values.activity.site &&
          (values.activity.site.title !== (initialData.activity as IActivity).site?.title ||
            values.activity.site.description !==
              (initialData.activity as IActivity).site?.description)
        ) {
          updateSite({
            variables: {
              id: values.activity.site.id,
              input: {
                siteType: values.activity.site.siteType,
                title: values.activity.site.title,
                locationCoordinates: values.activity.site.locationCoordinates,
                description: values.activity.site.description,
                externalId: values.activity.site.externalId,
              },
              lang: ('lang' in props && props.lang) || ELanguage.ENGLISH,
            },
          });
        }
      }

      const newActivityValues = extractActivityMutationValues(values);
      const hasActivityChanged = !isEqual(
        newActivityValues,
        extractActivityMutationValues(initialData)
      );
      if (hasActivityChanged && values.activity.id) {
        await updateActivity({
          variables: {
            id: values.activity.id,
            input: {
              ...newActivityValues,
              subActivities,
            },
            lang: !isChainActivity && props.lang ? props.lang : ELanguage.ENGLISH,
          },
        });
      }
    } else if ('activity' in values && values.activity) {
      // with the id in the response we can create the activity
      activityResponse = await handleCreateActivity({
        variables: {
          input: {
            ownedById: values.activity.ownedBy?.id || null,
            siteId: values.activity.site?.id,
            componentId: values.activity.component?.id,
            description: values.activity.description,
            title: values.activity.title,
            mediaList: values.activity.mediaList,
            subActivities,
          },
          lang: ELanguage.ENGLISH,
        },
      });
    }

    // for creating and updating chain activity
    if (isChainActivity && 'chainStepId' in values && 'incomingLinks' in values) {
      const initialActivityData = pick(initialData, ['chainStepId', 'incomingLinks']);
      const currentActivityData = pick(values, ['chainStepId', 'incomingLinks']);
      if (isEdit) {
        if (!isEqual(initialActivityData, currentActivityData)) {
          const changedKeys = difference(initialActivityData, currentActivityData);
          props.onChainActivityEdit({
            id: props.chainStepActivity.id,
            ...pick(currentActivityData, changedKeys),
          });
        }
      } else {
        if (!activityResponse?.data?.createActivity?.activity?.id) {
          throw new Error('Activity id is not defined');
        }
        props.onActivityAdd(activityResponse.data.createActivity.activity?.id, currentActivityData);
      }
    }
    onClose?.();
  };

  // If there are unsaved changes, ask the user if they want to leave the page
  const handleClose = () => {
    if (formRef.current && !isEqual(formRef.current.values, initialData)) {
      openDialog({
        type: 'ALERT',
        props: {
          title: 'Unsaved changes',
          text: 'Are you sure you want to close this window? All unsaved changes will be lost and you will not be able to undo this action.',
          submitText: 'Discard changes',
          displayCloseButton: true,
          onSubmit: onClose,
          onCancel: () => undefined,
        },
      });
    } else {
      onClose?.();
    }
  };

  const handleDeleteClick = () => {
    // Delete is only possible in edit mode
    if (!disableDelete && isEdit) {
      if (isChainActivity) {
        props.onActivityDelete(props.chainStepActivity.id);
      } else {
        handleRemoveActivity({ activityId: props.activity.id });
      }
      onClose?.();
    }
  };

  return (
    <DialogDefault
      title={!isEdit ? 'New activity' : 'Edit activity'}
      onClose={handleClose}
      data-testid="add-edit-activity-dialog"
      open={open}
      fullWidth
      icon={!isEdit ? Plus : Edit}
      maxWidth="xl"
    >
      <Formik
        innerRef={formRef}
        initialValues={initialData}
        validateOnChange
        initialStatus={{
          lang: isEdit && !isChainActivity && props.lang ? props.lang : ELanguage.ENGLISH,
        }}
        validationSchema={isChainActivity ? chainActivitySchema : nestedActivitySchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, isValid, values, initialValues }) => {
          return (
            <Form>
              <DialogInner container min-height="70vh">
                <FormContainerLeft item xs={6} maxHeight="85vh">
                  <BlockContainer container overflowY="auto">
                    <StepSelectorBlock
                      mode={editMode}
                      steps={isChainActivity ? props.steps : undefined}
                    />

                    {/* The site selector block is not shown in the product */}
                    <SiteSelectorBlock
                      mode={editMode}
                      disabled={!isEdit && !isChainActivity && !!props.site}
                    />

                    {isChainActivity && props.steps && props.activities && (
                      <LinkConnectionsBlock
                        steps={props.steps}
                        activities={props.activities}
                        subChains={props.subChains}
                      />
                    )}

                    {!hidePartnerSelector && (
                      <CompanySelectBlock
                        disabled={!isEdit && !isChainActivity && !!props.partner}
                      />
                    )}

                    <OutputComponentBlock mode={editMode} />

                    {isEdit && !isChainActivity && props.linkToSupplyChainMapping && (
                      <BottomContainer>
                        <InfoCircle size={14} />
                        <Box mr={1} />
                        <ThemeTypography variant="BODY_LARGE">
                          To edit supply chain details of this activity, go to the{' '}
                        </ThemeTypography>
                        <Box mr={0.5} />
                        <StyledLink to={props.linkToSupplyChainMapping} onClick={onClose}>
                          Supply chain mapping tool.
                        </StyledLink>
                      </BottomContainer>
                    )}
                  </BlockContainer>
                </FormContainerLeft>

                <FormContainerRight item xs={6}>
                  <BlockContainer
                    container
                    z-index={0}
                    height="calc(100% - 100px)"
                    overflowY="auto"
                  >
                    <ActivityCommunicationBlocks />
                  </BlockContainer>

                  <ButtonContainer justifyContent="space-between">
                    <Box>
                      {isEdit && !disableDelete && (
                        <ThemeButton
                          color="BLUE_ICE"
                          size="large"
                          type="button"
                          onClick={handleDeleteClick}
                          data-testid="delete-activity-form-btn"
                          startIcon={<Trash size={14} />}
                        >
                          Delete
                        </ThemeButton>
                      )}
                    </Box>

                    <Box display="flex" justifyContent="end" gap={1.8}>
                      <ThemeButton
                        color="BLUE_ICE"
                        size="large"
                        type="button"
                        onClick={handleClose}
                      >
                        Cancel
                      </ThemeButton>

                      <ThemeButton
                        data-testid="submit-activity-form-btn"
                        loading={isSubmitting}
                        color="PRIMARY"
                        size="large"
                        type="submit"
                        disabled={!isValid || isEqual(values, initialValues)}
                      >
                        {isEdit ? 'Apply changes' : 'Save'}
                      </ThemeButton>
                    </Box>
                  </ButtonContainer>
                </FormContainerRight>
              </DialogInner>
            </Form>
          );
        }}
      </Formik>
    </DialogDefault>
  );
};

export default AddEditActivityDialog;
